// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React from 'react';
import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { Nullable } from '../../../../../shared-types';
import { ClaimType } from '../../../../../claim/claim-types';
import { getClaim } from '../../../../../claim';

interface BaseProps {
    formDetails?: FormDetails;
    updateFormDetails?: UpdateFormDetails;
    disableEditButton?: boolean;
    claimType?: Nullable<ClaimType>;
}
interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const otherDetailsView: React.FunctionComponent<Props> = ({
    classes,
    formDetails,
    updateFormDetails,
    openModal,
    disableEditButton,
    claimType,
}: Props) => {
    const otherDetails = formDetails?.lifeAssured?.otherDetails;
    const healthInfo = formDetails?.lifeAssured?.healthInformation;

    return (
        <React.Fragment>
            <Grid container justify="space-between" className={classes.marginTop2}>
                <Grid container item xs={12} alignItems="center">
                    <Typography variant="h6" className={classes.heading}>
                        {claimType === ClaimType.Disability
                            ? 'Health Information'
                            : 'Other Details'}
                    </Typography>
                </Grid>
            </Grid>
            <Paper className={classes.paper} elevation={3}>
                {claimType !== ClaimType.Disability && (
                    <Grid container spacing={2}>
                        {/* Column One Start */}
                        <Grid container item xs={12} sm={6} direction="column">
                            <Grid item>
                                <LabeledValue
                                    label="What is your highest qualification?"
                                    value={otherDetails?.highestQualification}
                                />
                            </Grid>
                            <Grid item>
                                <LabeledValue
                                    label="Have you ever been declared insolvent or are sequestration hearing proceedings, pending or completed?"
                                    value={otherDetails?.hasInsolvency}
                                />
                            </Grid>
                            <Grid item>
                                <LabeledValue
                                    label="Do you consume alcohol?"
                                    value={otherDetails?.consumesAlcohol}
                                />
                            </Grid>
                            {otherDetails?.consumesAlcohol === 'Yes' && (
                                <>
                                    <Grid item>
                                        <LabeledValue
                                            label="How many spirits and tots per week?"
                                            value={otherDetails?.totCount}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <LabeledValue
                                            label="How many wines in a glass per week?"
                                            value={otherDetails?.wineGlassCount}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <LabeledValue
                                            label="How many beers/ciders in a can/bottle per week?"
                                            value={otherDetails?.beerCanOrCider}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item>
                                <LabeledValue
                                    label="Have you ever received advice to reduce or discontinue alcohol consumption, or have you ever been charged with drunken driving?"
                                    value={otherDetails?.receivedAdviceToReduceAlcohol}
                                />
                            </Grid>
                            {otherDetails?.receivedAdviceToReduceAlcohol === 'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full details"
                                        value={otherDetails?.alcoholAdviceDetails}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        {/* Column Two Start */}
                        <Grid container item xs={12} sm={6} direction="column">
                            <Grid item>
                                <LabeledValue
                                    label="Do you currently practice any avocation/s e.g scuba diving, flying, etc"
                                    value={otherDetails?.practiceAvocation}
                                />
                            </Grid>
                            {otherDetails?.practiceAvocation === 'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full details"
                                        value={otherDetails?.practiceAvocationDetails}
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <LabeledValue
                                    label="Please provide the average number of hours that you were working per week, for three months prior to the date of the claim event"
                                    value={otherDetails?.hoursWorkedPerWeek}
                                />
                            </Grid>
                            <Grid item>
                                <LabeledValue
                                    label="Were you partially or fully retired at the date of the claim event?"
                                    value={otherDetails?.wasRetired}
                                />
                            </Grid>
                            {otherDetails?.wasRetired === 'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full details"
                                        value={otherDetails?.retirementFullDetails}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}

                {claimType === ClaimType.Disability && (
                    <Grid container spacing={2}>
                        {/* Column One Start */}
                        <Grid container item xs={12} sm={6} direction="column">
                            <Grid item>
                                <LabeledValue
                                    label="What is your highest qualification?"
                                    value={healthInfo?.whatIsYourHighestQualification}
                                />
                            </Grid>
                            <Grid item>
                                <LabeledValue
                                    label="Do you consume alcohol?"
                                    value={healthInfo?.doYouConsumeAlcohol}
                                />
                            </Grid>
                            {healthInfo?.doYouConsumeAlcohol === 'Yes' && (
                                <>
                                    <Grid item>
                                        <LabeledValue
                                            label="How many wines in a glass per week?"
                                            value={healthInfo?.howManyWinesInAGlassPerWeek}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item>
                                <LabeledValue
                                    label="Have you ever been charged with drunken driving?"
                                    value={healthInfo?.haveYouEverBeenChargedWithDrunkenDriving}
                                />
                            </Grid>
                            {healthInfo?.haveYouEverBeenChargedWithDrunkenDriving === 'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full details of the drunken driving charge"
                                        value={
                                            healthInfo?.pleaseProvideFullDetailsOfDrunkenDrivingCharge
                                        }
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <LabeledValue
                                    label="Please provide the average number of hours that you were working per week, for three month prior to the date of the claim event"
                                    value={
                                        healthInfo?.pleaseProvideTheAverageNumberOfHoursThatYouWereWorkingPerWeek
                                    }
                                />
                            </Grid>
                            <Grid item>
                                <LabeledValue
                                    label="Were you partially or fully retired at the date of the claim event?"
                                    value={
                                        healthInfo?.wereYouPartiallyOrFullyRetiredAtTheDateOfTheClaimEvent
                                    }
                                />
                            </Grid>
                            {healthInfo?.wereYouPartiallyOrFullyRetiredAtTheDateOfTheClaimEvent ===
                                'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full retirement details"
                                        value={healthInfo?.retirementFullDetails}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        {/* Column Two Start */}
                        <Grid container item xs={12} sm={6} direction="column">
                            <Grid item>
                                <LabeledValue
                                    label="Have you ever been declared insolvent or are sequestration hearing proceedings pending or completed?"
                                    value={
                                        healthInfo?.haveYouEverBeenDeclaredInsolventOrAreSequestrationHearingProceedingsPendingOrCompleted
                                    }
                                />
                            </Grid>

                            {healthInfo?.doYouConsumeAlcohol === 'Yes' && (
                                <>
                                    <Grid item>
                                        <LabeledValue
                                            label="How many spirits and tots per week?"
                                            value={healthInfo?.howManySpiritsAndTotsPerWeek}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <LabeledValue
                                            label="How many beers or ciders in a can or bottle per week?"
                                            value={
                                                healthInfo?.howManyBeersOrCidersInACanOrBottlePerWeek
                                            }
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item>
                                <LabeledValue
                                    label="Have you ever received advice to reduce or discontinue alcohol consumption?"
                                    value={
                                        healthInfo?.haveYouEverReceivedAdviceToReduceOrDiscontinueAlcoholConsumption
                                    }
                                />
                            </Grid>

                            {healthInfo?.haveYouEverReceivedAdviceToReduceOrDiscontinueAlcoholConsumption ===
                                'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full details of the advice"
                                        value={healthInfo?.pleaseProvideFullDetailsOfTheAdvice}
                                    />
                                </Grid>
                            )}

                            <Grid item>
                                <LabeledValue
                                    label="Do you currently practice any avocation/s (e.g. scuba diving, flying)?"
                                    value={healthInfo?.doYouCurrentlyPracticeAnyAvocations}
                                />
                            </Grid>

                            {healthInfo?.doYouCurrentlyPracticeAnyAvocations === 'Yes' && (
                                <Grid item>
                                    <LabeledValue
                                        label="Please provide full details of the avocations"
                                        value={healthInfo?.pleaseProvideFullDetailsOfTheAvocations}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </React.Fragment>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails || getFormDetails(state),
    disableEditButton: ownProps.disableEditButton || false,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const OtherDetailsView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(otherDetailsView));
